import React, { createContext, useContext, useState, useEffect } from 'react';
import { facebookService, FacebookUser } from '../services/facebook';

interface UserContextType {
  user: FacebookUser | null;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Facebook App ID for biig.dev
const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID || '3211801755664069';

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<FacebookUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize Facebook SDK and check login status
    const initFacebook = async () => {
      try {
        await facebookService.init(FACEBOOK_APP_ID);
        const status = await facebookService.getLoginStatus();
        if (status.isLoggedIn && status.user) {
          setUser(status.user);
        }
      } catch (error) {
        console.error('Failed to initialize Facebook SDK:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initFacebook();
  }, []);

  const login = async () => {
    try {
      setIsLoading(true);
      const fbUser = await facebookService.login();
      if (fbUser) {
        setUser(fbUser);
        // Optionally save to backend
        console.log('User logged in:', fbUser);
      }
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await facebookService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UserContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};